export function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function roundTo(value, digits) {
  const p = 10 ** digits;
  return Math.round((toNumber(value) + Number.EPSILON) * p) / p;
}

export function pctToRatio(pct) {
  return toNumber(pct) / 100;
}

export function average(values) {
  const nums = (values || []).map((v) => toNumber(v));
  if (!nums.length) return 0;
  return nums.reduce((sum, n) => sum + n, 0) / nums.length;
}

export function calcProjectedSales(avgSales, growthPct) {
  return toNumber(avgSales) * (1 + pctToRatio(growthPct));
}

export function calcProjectedGc(avgGc, growthPct) {
  return toNumber(avgGc) * (1 + pctToRatio(growthPct));
}

export function calcProjectedQty(avgQty, growthPct) {
  return toNumber(avgQty) * (1 + pctToRatio(growthPct));
}

/** Projected GC ÷ 1000 (Excel PGC). */
export function calcPgc(projectedGc) {
  return toNumber(projectedGc) / 1000;
}

/** PGC × (1 + Buffer%). Excel Gc Buffer (column W). */
export function calcGcBuffer(projectedGc, bufferPct) {
  return calcPgc(projectedGc) * (1 + pctToRatio(bufferPct));
}

/** AVG UPT × Gc Buffer. Excel Build-to (T × W). */
export function calcBuildTo(avgUpt, gcBuffer) {
  return toNumber(avgUpt) * toNumber(gcBuffer);
}

/** (W-1+W-2+W-3 qty) ÷ (GC W-1+W-2+W-3) × 1000 */
export function calcAvgUptFromWeeks(weekQtys, weekGcs) {
  const qty = (weekQtys || []).reduce((sum, value) => sum + toNumber(value), 0);
  const gc = (weekGcs || []).reduce((sum, value) => sum + toNumber(value), 0);
  if (!gc) return 0;
  return (qty / gc) * 1000;
}

export function calcSuggestedOrder(buildTo, closingStock) {
  return toNumber(buildTo) - toNumber(closingStock);
}

export function calcFinalOrder(totalRequirement) {
  return Math.max(0, Math.round(toNumber(totalRequirement)));
}

/**
 * Split Final Order across SPS 1/2/3 using larger pack sizes first.
 * Leftover qty goes to the next smaller pack that fits.
 * Missing / 0 pack size → null (shown as —).
 */
export function calcSpsOrders(finalOrder, sps1, sps2, sps3) {
  const rawSizes = [sps1, sps2, sps3];
  const empty = rawSizes.map((raw) => raw == null || String(raw).trim() === '');
  const sizes = rawSizes.map((raw, index) => {
    if (empty[index]) return 0;
    const n = toNumber(raw);
    return n > 0 ? n : 0;
  });
  const ords = sizes.map((size, index) => (empty[index] || size <= 0 ? null : 0));
  if (finalOrder == null || finalOrder === '') {
    return { spsOrd1: ords[0], spsOrd2: ords[1], spsOrd3: ords[2] };
  }
  let remaining = toNumber(finalOrder);
  if (remaining <= 0) {
    return { spsOrd1: ords[0], spsOrd2: ords[1], spsOrd3: ords[2] };
  }
  const slotOrder = [0, 1, 2]
    .filter((index) => !empty[index] && sizes[index] > 0)
    .sort((a, b) => sizes[b] - sizes[a] || b - a);
  slotOrder.forEach((index) => {
    const size = sizes[index];
    const packs = Math.floor((remaining + 1e-9) / size);
    ords[index] = packs > 0 ? packs : 0;
    remaining = roundTo(remaining - packs * size, 6);
    if (remaining < 0) remaining = 0;
  });
  return { spsOrd1: ords[0], spsOrd2: ords[1], spsOrd3: ords[2] };
}

/** Weekly qty / guest count * 1000 */
export function calcUpt(weekQty, guestCount) {
  const gc = toNumber(guestCount);
  if (!gc) return 0;
  return (toNumber(weekQty) / gc) * 1000;
}

export function formatMoney(value) {
  if (value == null || value === '') return '—';
  const n = toNumber(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

export function formatQty(value, digits = 1) {
  if (value == null || value === '') return '—';
  const n = toNumber(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-IN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function formatInt(value) {
  if (value == null || value === '') return '—';
  const n = toNumber(value);
  if (!Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('en-IN');
}
